
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { Textarea } from '@/components/ui/textarea';
import { createShop, generateUniqueSlug } from '@/lib/supabase/shops';
import { toast } from 'sonner';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';

interface AddShopProps {
  onShopAdded: () => void;
  onCancel: () => void;
}

// Create a schema for form validation
const shopFormSchema = z.object({
  name: z.string().min(1, { message: "Le nom de la boutique est requis" }),
  description: z.string().optional(),
  category: z.string().optional(),
  slug: z.string().optional()
});

type FormValues = z.infer<typeof shopFormSchema>;

const AddShop: React.FC<AddShopProps> = ({ onShopAdded, onCancel }) => {
  const form = useForm<FormValues>({
    resolver: zodResolver(shopFormSchema),
    defaultValues: {
      name: '',
      description: '',
      category: '',
      slug: ''
    }
  });

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isGeneratingSlug, setIsGeneratingSlug] = React.useState(false);
  
  // Générer automatiquement un slug quand le nom change
  const updateSlug = async (name: string) => {
    if (!name) return;
    
    try {
      setIsGeneratingSlug(true);
      // Générer un slug unique
      const uniqueSlug = await generateUniqueSlug(name);
      form.setValue('slug', uniqueSlug);
    } catch (error) {
      console.error('Erreur lors de la génération du slug:', error);
      // En cas d'erreur, utiliser la méthode simple de génération de slug
      const simpleSlug = name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      form.setValue('slug', simpleSlug);
    } finally {
      setIsGeneratingSlug(false);
    }
  };

  useEffect(() => {
    const subscription = form.watch((value, { name: fieldName }) => {
      if (fieldName === 'name') {
        updateSlug(value.name || '');
      }
    });
    
    return () => subscription.unsubscribe();
  }, [form]);

  const handleSubmit = async (data: FormValues) => {
    try {
      setIsSubmitting(true);
      console.log("Creating shop with data:", data);
      
      // Créer la boutique avec la fonction createShop
      const result = await createShop({
        name: data.name,
        description: data.description || "",
        category: data.category || "",
        slug: data.slug || ""
      });
      
      console.log("Shop creation result:", result);
      
      if (result.success) {
        toast.success("Boutique créée avec succès");
        // Use the callback provided by parent component instead of reloading
        onShopAdded();
      } else {
        toast.error("Erreur lors de la création de la boutique");
        console.error('Error adding shop:', result.error);
      }
    } catch (error) {
      toast.error("Erreur lors de la création de la boutique");
      console.error('Exception adding shop:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nom de la boutique</FormLabel>
              <FormControl>
                <Input placeholder="Entrez le nom de la boutique" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="slug"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Slug URL</FormLabel>
              <FormControl>
                <div className="relative">
                  <Input 
                    placeholder="URL conviviale pour la boutique" 
                    {...field} 
                    disabled={isGeneratingSlug}
                  />
                  {isGeneratingSlug && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="h-4 w-4 border-t-2 border-b-2 border-gray-500 rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
              </FormControl>
              <FormMessage />
              <p className="text-xs text-gray-500">Le slug est utilisé dans l'URL (ex: votre-site.com/nom-de-boutique)</p>
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="category"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Catégorie</FormLabel>
              <FormControl>
                <Input placeholder="Catégorie de la boutique" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Description de la boutique" 
                  className="resize-none min-h-[100px]"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="flex justify-end space-x-4">
          <Button variant="outline" type="button" onClick={onCancel} disabled={isSubmitting}>
            Annuler
          </Button>
          <Button type="submit" disabled={isSubmitting || isGeneratingSlug}>
            {isSubmitting ? "Création en cours..." : "Ajouter"}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default AddShop;
